import { SortOrder } from '@Utils/helpers/sortingHelpers';
import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import _isNil from 'lodash/isNil';
import { GeoserverFeaturesResponse } from '@Data/geoserver/Interfaces';
import { RootState } from '@Services/redux/Store';
import { getSortedEntities } from './Helpers';
import {
    AnalysisEntityState,
    GetSpansResponse,
    IAnalysisEntity,
    IQcPole,
    IQcSpanEntity,
    IQcStatusOption,
    IReworkReasonStage,
    ISelectedPoleAttachedSpan,
    ImageIndex,
    QualityControlState,
    TableSortField,
} from './Interfaces';
import { IQualityControlState, initialQualityControlState } from './State';

const QualityControlSlice = createSlice({
    name: 'QualityControl',
    initialState: initialQualityControlState,
    reducers: {
        reset(state: IQualityControlState) {
            return {
                ...initialQualityControlState,
                geoserverData: state.geoserverData,
            };
        },
        setGeoserverData(
            state: IQualityControlState,
            action: PayloadAction<GeoserverFeaturesResponse>
        ) {
            state.geoserverData = action.payload;
        },
        setInspectionStartDate(
            state: IQualityControlState,
            action: PayloadAction<string>
        ) {
            state.inspectionStartDate = action.payload;
        },
        setState(
            state: IQualityControlState,
            action: PayloadAction<QualityControlState>
        ) {
            state.state = action.payload;
        },
        updateAnalysisEntities(
            state: IQualityControlState,
            action: PayloadAction<IAnalysisEntity[]>
        ) {
            state.analysisEntities = action.payload;
        },
        setSelectedAnalysisEntity(
            state: IQualityControlState,
            action: PayloadAction<IAnalysisEntity>
        ) {
            state.selectedAnalysisEntity = action.payload;
        },
        setTableSorting(
            state: IQualityControlState,
            action: PayloadAction<{ field?: TableSortField; order?: SortOrder }>
        ) {
            if (!_isNil(action.payload.field)) {
                state.tableSortField = action.payload.field;
            }

            if (!_isNil(action.payload.order)) {
                state.tableSortOrder = action.payload.order;
            }
        },
        resetSvgViewer(state: IQualityControlState) {
            state.backPole = initialQualityControlState.backPole;
            state.backSpan = initialQualityControlState.backSpan;
            state.centrePole = initialQualityControlState.centrePole;
            state.foreSpan = initialQualityControlState.foreSpan;
            state.forePole = initialQualityControlState.forePole;
        },
        setCurrentFullSpanData(state: IQualityControlState, action) {
            state.currentFullSpanData = action.payload;
        },
        updateBackPoleData(
            state: IQualityControlState,
            action: PayloadAction<IQcPole>
        ) {
            state.backPole = action.payload;
        },
        updateBackSpanData(
            state: IQualityControlState,
            action: PayloadAction<IQcSpanEntity>
        ) {
            state.backSpan = action.payload;
        },
        updateCentrePoleData(
            state: IQualityControlState,
            action: PayloadAction<IQcPole>
        ) {
            state.centrePole = action.payload;
        },
        updateForeSpanData(
            state: IQualityControlState,
            action: PayloadAction<IQcSpanEntity>
        ) {
            state.foreSpan = action.payload;
        },
        updateForePoleData(
            state: IQualityControlState,
            action: PayloadAction<IQcPole>
        ) {
            state.forePole = action.payload;
        },
        resetSvgImageIndices(state: IQualityControlState) {
            state.imageIndices = initialQualityControlState.imageIndices;
        },
        updateSvgImageIndex(
            state: IQualityControlState,
            action: PayloadAction<{
                imageIndex: ImageIndex;
                newIndex: ImageIndex;
            }>
        ) {
            state.imageIndices[action.payload.imageIndex] =
                action.payload.newIndex;
        },
        updateAllAttachedSpans(
            state: IQualityControlState,
            action: PayloadAction<ISelectedPoleAttachedSpan[]>
        ) {
            state.allAttachedSpans = action.payload;
        },
        updateImageLoadingByIndex(
            state: IQualityControlState,
            action: PayloadAction<{
                indices: ImageIndex | ImageIndex[];
                isLoading: boolean;
            }>
        ) {
            if (Array.isArray(action.payload.indices)) {
                action.payload.indices.forEach((index) => {
                    state.areImagesLoading[index] = action.payload.isLoading;
                });
            } else {
                state.areImagesLoading[action.payload.indices] =
                    action.payload.isLoading;
            }
        },
        updateAdjacentSpans(
            state: IQualityControlState,
            action: PayloadAction<ISelectedPoleAttachedSpan[]>
        ) {
            state.selectedPoleAdjacentSpans = action.payload;
        },
        updateSpanIndex(
            state: IQualityControlState,
            action: PayloadAction<{
                span: 'forespan' | 'backspan';
                index: number;
            }>
        ) {
            if (action.payload.span === 'forespan') {
                state.forespanIndex = action.payload.index;
            } else {
                state.backspanIndex = action.payload.index;
            }
        },
        updateReworkStageTopicReasons(
            state: IQualityControlState,
            action: PayloadAction<IReworkReasonStage[]>
        ) {
            state.reworkStages = action.payload;
        },
        updateSelectedAnalysisEntity(
            state: IQualityControlState,
            action: PayloadAction<{
                entity: IAnalysisEntity;
                keepState?: boolean;
            }>
        ) {
            const entity = action.payload.entity;

            const entityIndex = state.analysisEntities.findIndex(
                (e) => e.entity_version_id === entity.entity_version_id
            );

            if (!action.payload.keepState) {
                const existingEntity = state.analysisEntities[entityIndex];
                // set new state based on previous state
                let newState;
                if (existingEntity.qcState === AnalysisEntityState.PASS) {
                    newState =
                        entity.reworkReasons.length > 0
                            ? AnalysisEntityState.REWORK
                            : AnalysisEntityState.PASS;
                } else {
                    newState =
                        entity.reworkReasons.length > 0
                            ? AnalysisEntityState.REWORK
                            : AnalysisEntityState.TODO;
                }

                entity.qcState = newState;
            }

            state.analysisEntities[entityIndex] = entity;
            state.selectedAnalysisEntity = entity;
        },
        updateQcStatusOptions(
            state: IQualityControlState,
            action: PayloadAction<IQcStatusOption[]>
        ) {
            state.qcStatusOptions = action.payload;
        },
        setRawData(
            state: IQualityControlState,
            action: PayloadAction<GetSpansResponse>
        ) {
            state.rawData = action.payload;
        },
    },
});

export const QualityControlActions = QualityControlSlice.actions;

export const qualityControlGeoserverDataState = (state: RootState) =>
    state.qualityControl.geoserverData;
export const qualityControlGeoserverFeaturesState = createSelector(
    qualityControlGeoserverDataState,
    (geoserverData) => geoserverData?.features ?? []
);

export const qualityControlStateState = (state: RootState) =>
    state.qualityControl.state;
const baseAnalysisEntitiesState = (state: RootState) =>
    state.qualityControl.analysisEntities;
export const selectedAnalysisEntityState = (state: RootState) =>
    state.qualityControl.selectedAnalysisEntity;

export const analysisEntityTableSortOrderState = (state: RootState) =>
    state.qualityControl.tableSortOrder;
export const analysisEntityTableSortFieldState = (state: RootState) =>
    state.qualityControl.tableSortField;

export const analysisEntitiesState = createSelector(
    baseAnalysisEntitiesState,
    analysisEntityTableSortFieldState,
    analysisEntityTableSortOrderState,
    (entities, sortField, sortOrder) =>
        getSortedEntities(entities, sortOrder, sortField)
);

// export const shuffledEntitiesState = createSelector(
//     baseAnalysisEntitiesState,
//     (entities) => shuffleAnalysisEntities(entities)
// );

export const assessedAnalysisEntitiesState = createSelector(
    baseAnalysisEntitiesState,
    (entities) => entities.filter((e) => e.qcState !== AnalysisEntityState.TODO)
);

export const imageIndicesState = (state: RootState) =>
    state.qualityControl.imageIndices;
export const areImagesLoadingState = (state: RootState) =>
    state.qualityControl.areImagesLoading;

export const backPoleState = (state: RootState) =>
    state.qualityControl.backPole;
export const backSpanState = (state: RootState) =>
    state.qualityControl.backSpan;
export const centrePoleState = (state: RootState) =>
    state.qualityControl.centrePole;
export const foreSpanState = (state: RootState) =>
    state.qualityControl.foreSpan;
export const forePoleState = (state: RootState) =>
    state.qualityControl.forePole;
export const svgViewerDataState = createSelector(
    backPoleState,
    backSpanState,
    centrePoleState,
    foreSpanState,
    forePoleState,
    (backPole, backSpan, centrePole, foreSpan, forePole) => {
        return [backPole, backSpan, centrePole, foreSpan, forePole];
    }
);

export const adjacentSpansState = (state: RootState) =>
    state.qualityControl.selectedPoleAdjacentSpans;

export const svgImageUrlsState = createSelector(
    backPoleState,
    backSpanState,
    centrePoleState,
    foreSpanState,
    forePoleState,
    (backPole, backSpan, centrePole, foreSpan, forePole) => {
        return [
            backPole.truesizeImageUrls,
            backSpan.truesizeImageUrls,
            centrePole.truesizeImageUrls,
            foreSpan.truesizeImageUrls,
            forePole.truesizeImageUrls,
        ];
    }
);

export const centrePoleTagImages = createSelector(
    centrePoleState,
    (centrePole) => {
        return centrePole.poletagImageUrls;
    }
);

export const centrePoleLoadedTagImage = createSelector(
    centrePoleState,
    (centrePole) => {
        return centrePole.poletagLoadedImageUrl;
    }
);

export const backSpanDataState = createSelector(
    imageIndicesState,
    backSpanState,
    (imgIndices, backSpan) => {
        if (
            _isNil(imgIndices) ||
            _isNil(backSpan) ||
            backSpan.truesizeImageInfo.length === 0
        ) {
            return null;
        }

        return backSpan.truesizeImageInfo[imgIndices[1]];
    }
);

export const foreSpanDataState = createSelector(
    imageIndicesState,
    foreSpanState,
    (imgIndices, foreSpan) => {
        if (
            _isNil(imgIndices) ||
            _isNil(foreSpan) ||
            foreSpan.truesizeImageInfo.length === 0
        ) {
            return null;
        }

        return foreSpan.truesizeImageInfo[imgIndices[3]];
    }
);

export const allAttachedSpansState = (state: RootState) =>
    state.qualityControl.allAttachedSpans;

export const allOtherSpansState = createSelector(
    allAttachedSpansState,
    foreSpanState,
    backSpanState,
    (spans, foreSpan, backSpan) => {
        return spans.filter((s) => {
            return (
                s.span_id !== foreSpan.span_id && s.span_id !== backSpan.span_id
            );
        });
    }
);

export const hasCentrePoleState = createSelector(centrePoleState, (cp) => {
    return !_isNil(cp) && cp.truesizeImageUrls.length > 0;
});

export const reworkStagesState = (state: RootState) =>
    state.qualityControl.reworkStages;
export const allReworkReasonsState = createSelector(
    reworkStagesState,
    (stages) => {
        let output = [];
        stages.forEach((stage) => {
            if (stage.topics) {
                stage.topics.forEach((topic) => {
                    if (topic.reasons && Array.isArray(topic.reasons)) {
                        output = [...output, ...topic.reasons];
                    }
                });
            }
        });
        return output;
    }
);

export const allReworkTopicsState = createSelector(
    reworkStagesState,
    (stages) => {
        let output = [];
        stages.forEach((stage) => {
            output = [...output, ...stage.topics];
        });
        return output;
    }
);

export const qcStatusOptionsState = (state: RootState) =>
    state.qualityControl.qcStatusOptions;

export const inspectedRateState = createSelector(
    analysisEntitiesState,
    (analysisEntities) => {
        if (!analysisEntities || analysisEntities.length === 0) return 0;

        const inspected = analysisEntities.filter(
            (e) => e.qcState !== AnalysisEntityState.TODO
        );

        return inspected.length / analysisEntities.length;
    }
);

export const yieldRateState = createSelector(
    analysisEntitiesState,
    (analysisEntities) => {
        if (!analysisEntities || analysisEntities.length === 0) return 0;

        const total = analysisEntities.filter(
            (a) => a.qcState !== AnalysisEntityState.TODO
        );

        if (total.length === 0) return 0;

        const passed = analysisEntities.filter(
            (a) => a.qcState === AnalysisEntityState.PASS
        );

        return passed.length / total.length;
    }
);

export default QualityControlSlice.reducer;
